import React, { useState, useEffect } from 'react';
import FAQManagement from './FAQManagement';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface TenantDashboardProps {
  user?: {
    id: string;
    email: string;
    approved: boolean;
  };
}

interface IntegrationStatus {
  connected: boolean;
  data?: any;
}

const TenantDashboard: React.FC<TenantDashboardProps> = ({ user }) => {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [whatsappStatus, setWhatsappStatus] = useState<IntegrationStatus>({ connected: false });
  const [googleStatus, setGoogleStatus] = useState<IntegrationStatus>({ connected: false });
  const [odooStatus, setOdooStatus] = useState<IntegrationStatus>({ connected: false });
  const [shopifyStatus, setShopifyStatus] = useState<IntegrationStatus>({ connected: false });

  // Modal states
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showOdooModal, setShowOdooModal] = useState(false);
  const [showShopifyModal, setShowShopifyModal] = useState(false);

  // WhatsApp form state
  const [whatsappForm, setWhatsappForm] = useState({
    phoneNumber: '',
    phoneNumberId: '',
    accessToken: '',
    verifyToken: '',
  });
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappError, setWhatsappError] = useState('');

  // Odoo form state
  const [odooForm, setOdooForm] = useState({
    baseUrl: '',
    dbName: '',
    username: '',
    apiKey: '',
  });
  const [odooLoading, setOdooLoading] = useState(false);
  const [odooError, setOdooError] = useState('');
  const [odooSuccess, setOdooSuccess] = useState('');

  // Shopify form state
  const [shopifyForm, setShopifyForm] = useState({
    shop: '',
  });

  // Tenant preferences state
  const [tenantPreferences, setTenantPreferences] = useState({
    tone: '',
    timezone: '',
  });
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [preferencesError, setPreferencesError] = useState('');
  const [preferencesSuccess, setPreferencesSuccess] = useState('');

  useEffect(() => {
    const fetchTenantId = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token || !user?.id) return;

        const response = await fetch(`${API_URL}/user/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.tenant?.id) {
            setTenantId(data.data.tenant.id);
            localStorage.setItem('tenantID', data.data.tenant.id);
          }
          // Load tenant preferences
          if (data.data?.tenant) {
            setTenantPreferences({
              tone: data.data.tenant.tone || '',
              timezone: data.data.tenant.timezone || '',
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch tenant ID:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantId();
  }, [user, API_URL]);

  useEffect(() => {
    if (tenantId) {
      fetchAllStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Check if we're returning from OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state && tenantId) {
      // OAuth callback - status will be updated after redirect
      // Remove query params from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh statuses after a short delay
      setTimeout(() => {
        fetchAllStatuses();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchAllStatuses = async () => {
    if (!tenantId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      // Fetch WhatsApp status
      const whatsappRes = await fetch(`${API_URL}/whatsapp?tenantID=${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (whatsappRes.ok) {
        const whatsappData = await whatsappRes.json();
        setWhatsappStatus({ connected: true, data: whatsappData.data });
      } else {
        setWhatsappStatus({ connected: false });
      }

      // Fetch Google status
      const googleRes = await fetch(`${API_URL}/google/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (googleRes.ok) {
        const googleData = await googleRes.json();
        setGoogleStatus({ connected: googleData.data.connected, data: googleData.data });
      }

      // Fetch Odoo status
      const odooRes = await fetch(`${API_URL}/odoo/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (odooRes.ok) {
        const odooData = await odooRes.json();
        setOdooStatus({ connected: odooData.data.connected, data: odooData.data });
      }

      // Fetch Shopify status
      const shopifyRes = await fetch(`${API_URL}/shopify/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (shopifyRes.ok) {
        const shopifyData = await shopifyRes.json();
        setShopifyStatus({ connected: shopifyData.data.connected, data: shopifyData.data });
      }
    } catch (error) {
      console.error('Failed to fetch integration statuses:', error);
    }
  };

  useEffect(() => {
    // Listen for messages from OAuth popup window
    const handleMessage = (event: MessageEvent) => {
      // In production, you should verify event.origin for security
      if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
        // Refresh Google status after successful connection
        if (tenantId) {
          fetchAllStatuses();
        }
      } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
        window.alert(`Google OAuth error: ${event.data.error || 'Unknown error'}`);
      } else if (event.data.type === 'SHOPIFY_OAUTH_SUCCESS') {
        // Refresh Shopify status after successful connection
        if (tenantId) {
          fetchAllStatuses();
        }
      } else if (event.data.type === 'SHOPIFY_OAUTH_ERROR') {
        window.alert(`Shopify OAuth error: ${event.data.error || 'Unknown error'}`);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const handleGoogleConnect = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.alert('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_URL}/google/oauth/url`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Open OAuth URL in a popup window
        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(
          data.data.authUrl,
          'Google OAuth',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        // Check if popup was blocked
        if (!popup) {
          window.alert('Popup blocked. Please allow popups for this site and try again.');
          return;
        }

        // Note: We don't check popup.closed due to Cross-Origin-Opener-Policy restrictions
        // The popup will communicate via postMessage when done
      } else {
        const errorData = await response.json();
        window.alert(errorData.error?.message || 'Failed to get Google OAuth URL');
      }
    } catch (error) {
      console.error('Failed to connect Google:', error);
      window.alert('Failed to connect Google');
    }
  };

  const handleShopifyConnectClick = () => {
    // Pre-fill form if already configured
    if (shopifyStatus.connected && shopifyStatus.data) {
      setShopifyForm({
        shop: shopifyStatus.data.shopName || '',
      });
    } else {
      setShopifyForm({
        shop: '',
      });
    }
    setShowShopifyModal(true);
  };

  const handleShopifyConnect = async () => {
    if (!shopifyForm.shop.trim()) {
      window.alert('Please enter your Shopify store name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.alert('Authentication required. Please log in again.');
        return;
      }

      const shop = shopifyForm.shop.trim();
      // Remove .myshopify.com if user included it
      const shopName = shop.replace('.myshopify.com', '');

      const response = await fetch(`${API_URL}/shopify/oauth/authorize?shop=${encodeURIComponent(shopName)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setShowShopifyModal(false);

        // Open OAuth URL in a popup window
        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(
          data.data.authUrl,
          'Shopify OAuth',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        // Check if popup was blocked
        if (!popup) {
          window.alert('Popup blocked. Please allow popups for this site and try again.');
          return;
        }

        // Note: We don't check popup.closed due to Cross-Origin-Opener-Policy restrictions
        // The popup will communicate via postMessage when done
      } else {
        const errorData = await response.json();
        window.alert(errorData.error?.message || 'Failed to get Shopify OAuth URL');
      }
    } catch (error) {
      console.error('Failed to connect Shopify:', error);
      window.alert('Failed to connect Shopify');
    }
  };

  const handleOdooConfigureClick = () => {
    // Pre-fill form if already configured
    if (odooStatus.connected && odooStatus.data) {
      setOdooForm({
        baseUrl: odooStatus.data.baseUrl || '',
        dbName: odooStatus.data.dbName || '',
        username: odooStatus.data.username || '',
        apiKey: odooStatus.data.apiKey || ''
      });
    } else {
      setOdooForm({
        baseUrl: '',
        dbName: '',
        username: '',
        apiKey: '',
      });
    }
    setOdooError('');
    setOdooSuccess('');
    setShowOdooModal(true);
  };

  const handleOdooSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setOdooError('');
    setOdooSuccess('');
    setOdooLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setOdooError('Authentication required. Please log in again.');
        setOdooLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/odoo/configure`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(odooForm),
      });

      if (response.ok) {
        setOdooSuccess('Odoo configuration saved successfully!');
        // Refresh statuses after a short delay to show success message
        setTimeout(() => {
          setShowOdooModal(false);
          fetchAllStatuses();
          setOdooSuccess('');
        }, 1500);
      } else {
        const errorData = await response.json();
        setOdooError(errorData.error?.message || 'Failed to configure Odoo');
      }
    } catch (error) {
      console.error('Failed to configure Odoo:', error);
      setOdooError('Failed to configure Odoo. Please check your connection and try again.');
    } finally {
      setOdooLoading(false);
    }
  };

  const handleWhatsAppConfigureClick = () => {
    // Pre-fill form if already configured
    if (whatsappStatus.connected && whatsappStatus.data) {
      setWhatsappForm({
        phoneNumber: whatsappStatus.data.phoneNumber || '',
        phoneNumberId: whatsappStatus.data.phoneNumberId || '',
        accessToken: whatsappStatus.data.accessToken || '',
        verifyToken: whatsappStatus.data.verifyToken || '',
      });
    } else {
      setWhatsappForm({
        phoneNumber: '',
        phoneNumberId: '',
        accessToken: '',
        verifyToken: '',
      });
    }
    setWhatsappError('');
    setShowWhatsAppModal(true);
  };

  const handleWhatsAppSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;

    setWhatsappError('');
    setWhatsappLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/whatsapp/configure?tenantID=${tenantId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: whatsappForm.phoneNumber,
          phoneNumberId: whatsappForm.phoneNumberId,
          accessToken: whatsappForm.accessToken,
          verifyToken: whatsappForm.verifyToken || undefined,
        }),
      });

      if (response.ok) {
        setShowWhatsAppModal(false);
        fetchAllStatuses();
      } else {
        const errorData = await response.json();
        setWhatsappError(errorData.error?.message || 'Failed to configure WhatsApp');
      }
    } catch (error) {
      console.error('Failed to configure WhatsApp:', error);
      setWhatsappError('Failed to configure WhatsApp');
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handlePreferencesUpdate = async () => {
    try {
      setPreferencesLoading(true);
      setPreferencesError('');
      setPreferencesSuccess('');

      const token = localStorage.getItem('token');
      if (!token) {
        window.alert('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_URL}/user/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tone: tenantPreferences.tone || undefined,
          timezone: tenantPreferences.timezone || undefined,
        }),
      });

      if (response.ok) {
        setPreferencesSuccess('Preferences updated successfully!');
        setTimeout(() => {
          setShowPreferencesModal(false);
          setPreferencesSuccess('');
        }, 1500);
      } else {
        const errorData = await response.json();
        setPreferencesError(errorData.error?.message || 'Failed to update preferences');
      }
    } catch (error) {
      console.error('Failed to update preferences:', error);
      setPreferencesError('Failed to update preferences');
    } finally {
      setPreferencesLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full p-8">
        <div className="bg-gradient-to-br from-main-medium to-main-dark rounded-2xl p-8 shadow-xl">
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Count connected integrations (excluding WhatsApp since it's separate now)
  const connectedIntegrations = [googleStatus, odooStatus, shopifyStatus].filter(s => s.connected).length;

  return (
    <div className="w-full p-8 space-y-8">
      {/* WhatsApp Configuration Section - Main Focus */}
      <div className="bg-gradient-to-br from-blue-600/20 to-blue-700/20 backdrop-blue-sm rounded-2xl p-8 shadow-xl border border-blue-500/20">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white tracking-tight">
                WhatsApp Business API
              </h2>
              <p className="text-gray-400 text-sm">Your primary communication channel</p>
            </div>
          </div>
          <span className={`px-3 py-1.5 text-sm font-semibold rounded-full border ${whatsappStatus.connected ? 'bg-green-500/20 text-green-400 border-green-400/30' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'}`}>
            {whatsappStatus.connected ? 'Connected' : 'Setup Required'}
          </span>
        </div>

        {whatsappStatus.connected ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-5 shadow-lg border border-white/5">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-gray-400 text-sm">Phone Number</span>
                </div>
                <p className="text-white font-semibold text-lg">{whatsappStatus.data?.phoneNumber || 'N/A'}</p>
              </div>
              <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-5 shadow-lg border border-white/5">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                  </svg>
                  <span className="text-gray-400 text-sm">Phone Number ID</span>
                </div>
                <p className="text-white font-semibold text-lg truncate">{whatsappStatus.data?.phoneNumberId || 'N/A'}</p>
              </div>
              <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-5 shadow-lg border border-white/5">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <span className="text-gray-400 text-sm">Access Token</span>
                </div>
                <p className="text-white font-semibold">{whatsappStatus.data?.accessToken ? '••••••••' : 'Not set'}</p>
              </div>
              <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-5 shadow-lg border border-white/5">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <span className="text-gray-400 text-sm">Verify Token</span>
                </div>
                <p className="text-white font-semibold">{whatsappStatus.data?.verifyToken ? '••••••••' : 'Not set'}</p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleWhatsAppConfigureClick}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Update Configuration
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-6">
              <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Connect Your WhatsApp Business Account</h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Configure your WhatsApp Business API to start receiving and sending messages through the platform.
            </p>
            <button
              onClick={handleWhatsAppConfigureClick}
              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center gap-2 mx-auto"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Configure WhatsApp
            </button>
          </div>
        )}
      </div>


      {/* Preferences Section */}
      <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-8 shadow-xl backdrop-blur-sm">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white tracking-tight">
              Preferences
            </h2>
          </div>
          <button
            onClick={() => setShowPreferencesModal(true)}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            Update Preferences
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 border border-white/5">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">Tone</h3>
            </div>
            <p className="text-gray-400 text-sm ml-11">
              {tenantPreferences.tone ? (
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 text-xs font-semibold rounded-full border border-purple-500/30 capitalize">
                  {tenantPreferences.tone}
                </span>
              ) : (
                <span className="text-gray-500">Not set</span>
              )}
            </p>
          </div>
          <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 border border-white/5">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">Timezone</h3>
            </div>
            <p className="text-gray-400 text-sm ml-11">
              {tenantPreferences.timezone ? (
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 text-xs font-semibold rounded-full border border-blue-500/30">
                  {tenantPreferences.timezone}
                </span>
              ) : (
                <span className="text-gray-500">Not set</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Integrations Section */}
      <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-8 shadow-xl backdrop-blur-sm">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white tracking-tight">
              Integrations
            </h2>
            <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-semibold rounded-full border border-purple-500/30">
              {connectedIntegrations}/3 connected
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Google */}
          <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.01] border border-white/5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg shadow-md ${googleStatus.connected ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gradient-to-br from-gray-500 to-gray-600'}`}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">Google</h3>
              </div>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${googleStatus.connected ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
                {googleStatus.connected ? 'Connected' : 'Not connected'}
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-4 ml-13">
              {googleStatus.connected
                ? googleStatus.data?.email || 'N/A'
                : 'Connect your Google account'}
            </p>
            <button
              onClick={handleGoogleConnect}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
            >
              {googleStatus.connected ? 'Reconnect Google' : 'Connect Google'}
            </button>
          </div>

          {/* Odoo */}
          <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.01] border border-white/5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg shadow-md ${odooStatus.connected ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gradient-to-br from-gray-500 to-gray-600'}`}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">Odoo</h3>
              </div>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${odooStatus.connected ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
                {odooStatus.connected ? 'Connected' : 'Not connected'}
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-4 ml-13">
              {odooStatus.connected
                ? odooStatus.data?.baseUrl || 'N/A'
                : 'Connect your Odoo ERP system'}
            </p>
            <button
              onClick={handleOdooConfigureClick}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
            >
              {odooStatus.connected ? 'Update Configuration' : 'Configure Odoo'}
            </button>
          </div>

          {/* Shopify */}
          <div className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.01] border border-white/5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg shadow-md ${shopifyStatus.connected ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gradient-to-br from-gray-500 to-gray-600'}`}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">Shopify</h3>
              </div>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${shopifyStatus.connected ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
                {shopifyStatus.connected ? 'Connected' : 'Not connected'}
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-4 ml-13">
              {shopifyStatus.connected
                ? shopifyStatus.data?.shopName || 'N/A'
                : 'Connect your Shopify store'}
            </p>
            <button
              onClick={handleShopifyConnectClick}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
            >
              {shopifyStatus.connected ? 'Reconnect Shopify' : 'Connect Shopify'}
            </button>
          </div>
        </div>
      </div>

      {/* FAQ Management Component */}
      <FAQManagement tenantId={tenantId} />

      {/* WhatsApp Configuration Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Configure WhatsApp</h3>
              <button
                onClick={() => setShowWhatsAppModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleWhatsAppSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    value={whatsappForm.phoneNumber}
                    onChange={(e) => setWhatsappForm({ ...whatsappForm, phoneNumber: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Phone Number ID *
                  </label>
                  <input
                    type="text"
                    value={whatsappForm.phoneNumberId}
                    onChange={(e) => setWhatsappForm({ ...whatsappForm, phoneNumberId: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Access Token *
                  </label>
                  <input
                    type="password"
                    value={whatsappForm.accessToken}
                    onChange={(e) => setWhatsappForm({ ...whatsappForm, accessToken: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Verify Token (Optional)
                  </label>
                  <input
                    type="password"
                    value={whatsappForm.verifyToken}
                    onChange={(e) => setWhatsappForm({ ...whatsappForm, verifyToken: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                  />
                </div>
                {whatsappError && (
                  <div className="p-4 bg-red-500/10 backdrop-blur-sm rounded-xl text-red-200 text-sm shadow-lg border-l-4 border-red-500">
                    {whatsappError}
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowWhatsAppModal(false)}
                    className="flex-1 bg-main-light/50 hover:bg-main-light/70 text-white font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={whatsappLoading}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    {whatsappLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Odoo Configuration Modal */}
      {showOdooModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Configure Odoo</h3>
              <button
                onClick={() => setShowOdooModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleOdooSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Base URL *
                  </label>
                  <input
                    type="url"
                    value={odooForm.baseUrl}
                    onChange={(e) => setOdooForm({ ...odooForm, baseUrl: e.target.value })}
                    placeholder="https://odoo.example.com"
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Database Name *
                  </label>
                  <input
                    type="text"
                    value={odooForm.dbName}
                    onChange={(e) => setOdooForm({ ...odooForm, dbName: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    value={odooForm.username}
                    onChange={(e) => setOdooForm({ ...odooForm, username: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    API Key *
                  </label>
                  <input
                    type="password"
                    value={odooForm.apiKey}
                    onChange={(e) => setOdooForm({ ...odooForm, apiKey: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    required
                  />
                </div>
                {odooError && (
                  <div className="p-4 bg-red-500/10 backdrop-blur-sm rounded-xl text-red-200 text-sm shadow-lg border-l-4 border-red-500">
                    {odooError}
                  </div>
                )}
                {odooSuccess && (
                  <div className="p-4 bg-green-500/10 backdrop-blur-sm rounded-xl text-green-200 text-sm shadow-lg border-l-4 border-green-500">
                    {odooSuccess}
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowOdooModal(false)}
                    className="flex-1 bg-main-light/50 hover:bg-main-light/70 text-white font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={odooLoading}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    {odooLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shopify Connection Modal */}
      {showShopifyModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Connect Shopify</h3>
              <button
                onClick={() => setShowShopifyModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleShopifyConnect(); }}>
              <div className="space-y-4">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
                  <p className="text-sm text-blue-300">
                    <span className="font-semibold">Important:</span> Make sure you're logged into your Shopify admin before connecting.{' '}
                    {shopifyForm.shop && (
                      <a
                        href={`https://${shopifyForm.shop.replace('.myshopify.com', '')}.myshopify.com/admin`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-blue-200"
                      >
                        Log in to Shopify
                      </a>
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Shopify Store Name *
                  </label>
                  <input
                    type="text"
                    value={shopifyForm.shop}
                    onChange={(e) => setShopifyForm({ ...shopifyForm, shop: e.target.value })}
                    placeholder="mystore or mystore.myshopify.com"
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Enter your store name (e.g., "mystore" or "mystore.myshopify.com")
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowShopifyModal(false)}
                    className="flex-1 bg-main-light/50 hover:bg-main-light/70 text-white font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-2.5 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    Connect
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferencesModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Update Preferences</h3>
              <button
                onClick={() => {
                  setShowPreferencesModal(false);
                  setPreferencesError('');
                  setPreferencesSuccess('');
                }}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handlePreferencesUpdate(); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Tone
                  </label>
                  <select
                    value={tenantPreferences.tone}
                    onChange={(e) => setTenantPreferences({ ...tenantPreferences, tone: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                  >
                    <option value="">Select tone...</option>
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="casual">Casual</option>
                    <option value="formal">Formal</option>
                    <option value="conversational">Conversational</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Select the communication tone for your automated messages
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Timezone
                  </label>
                  <select
                    value={tenantPreferences.timezone}
                    onChange={(e) => setTenantPreferences({ ...tenantPreferences, timezone: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                  >
                    <option value="">Select timezone...</option>
                    <option value="UTC">UTC (Coordinated Universal Time)</option>
                    <option value="America/New_York">America/New_York (EST/EDT)</option>
                    <option value="America/Chicago">America/Chicago (CST/CDT)</option>
                    <option value="America/Denver">America/Denver (MST/MDT)</option>
                    <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                    <option value="Europe/London">Europe/London (GMT/BST)</option>
                    <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                    <option value="Europe/Berlin">Europe/Berlin (CET/CEST)</option>
                    <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                    <option value="Asia/Shanghai">Asia/Shanghai (CST)</option>
                    <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                    <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                    <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Select your timezone for accurate time-based operations
                  </p>
                </div>
                {preferencesError && (
                  <div className="p-4 bg-red-500/10 backdrop-blur-sm rounded-xl text-red-200 text-sm shadow-lg border-l-4 border-red-500">
                    {preferencesError}
                  </div>
                )}
                {preferencesSuccess && (
                  <div className="p-4 bg-green-500/10 backdrop-blur-sm rounded-xl text-green-200 text-sm shadow-lg border-l-4 border-green-500">
                    {preferencesSuccess}
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPreferencesModal(false);
                      setPreferencesError('');
                      setPreferencesSuccess('');
                    }}
                    className="flex-1 bg-main-light/50 hover:bg-main-light/70 text-white font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={preferencesLoading}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    {preferencesLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantDashboard;
